import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DollarSign, Coins, Copy, Check, Heart } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
interface DonateModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const CRYPTO_WALLET_ADDRESS = '0x8d037F49B089988f7950e00A4c9a94F9d379af7f';
export function DonateModal({
  isOpen,
  onClose
}: DonateModalProps) {
  const [showCryptoStep, setShowCryptoStep] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) {
      setShowCryptoStep(false);
      setCopied(false);
    }
  }, [isOpen]);
  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(CRYPTO_WALLET_ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const handleDonate = (method: 'paypal' | 'crypto') => {
    if (method === 'crypto') {
      setShowCryptoStep(true);
      return;
    }
    // PayPal hosted button
    const paypalLink = 'https://www.paypal.com/donate/?hosted_button_id=Y5VPL5ZQQJE4U';
    window.open(paypalLink, '_blank');
  };
  const handleBack = () => {
    setShowCryptoStep(false);
    setCopied(false);
  };
  if (!isOpen) return null;
  const modalContent = <AnimatePresence data-id="element-226">
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-id="element-227">
        {/* Dark Blurred Overlay */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={onClose} data-id="element-228" />

        {/* Modal Content */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-229">
          <GlassCard className="relative border-white/10" data-id="element-230">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-gray-400 hover:text-white z-10" data-id="element-231">
              <X size={20} data-id="element-232" />
            </button>

            <div className="p-8" data-id="element-233">
              {!showCryptoStep ? <>
                  {/* Header */}
                  <div className="text-center mb-8" data-id="element-234">
                    <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: 0.1,
                  type: 'spring',
                  stiffness: 200
                }} className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 mb-4" data-id="element-235">
                      <Heart size={32} className="text-pink-400" data-id="element-236" />
                    </motion.div>
                    <h2 className="text-3xl font-bold text-white mb-2 font-special" data-id="element-237">
                      Support Faind
                    </h2>
                    <p className="text-gray-400 text-sm" data-id="element-238">
                      Help us keep the directory free and updated daily with the
                      latest AI tools
                    </p>
                  </div>

                  {/* Payment Methods */}
                  <div className="space-y-3" data-id="element-239">
                    {/* PayPal */}
                    <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => handleDonate('paypal')} className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-blue-500/30 transition-all group" data-id="element-240">
                      <div className="flex items-center gap-4" data-id="element-241">
                        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 group-hover:border-blue-500/50 transition-all" data-id="element-242">
                          <DollarSign size={24} className="text-blue-400" data-id="element-243" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-244">
                          <div className="font-bold text-white mb-0.5" data-id="element-245">
                            PayPal
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-246">
                            Credit cards, debit cards & Apple Pay
                          </div>
                        </div>
                        <div className="text-blue-400 font-bold" data-id="element-247">→</div>
                      </div>
                    </motion.button>

                    {/* Crypto */}
                    <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => handleDonate('crypto')} className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-orange-500/30 transition-all group" data-id="element-248">
                      <div className="flex items-center gap-4" data-id="element-249">
                        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border border-orange-500/30 group-hover:border-orange-500/50 transition-all" data-id="element-250">
                          <Coins size={24} className="text-orange-400" data-id="element-251" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-252">
                          <div className="font-bold text-white mb-0.5" data-id="element-253">
                            Cryptocurrency
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-254">
                            ETH, USDT, USDC & more
                          </div>
                        </div>
                        <div className="text-orange-400 font-bold" data-id="element-255">→</div>
                      </div>
                    </motion.button>
                  </div>

                  {/* Footer note */}
                  <p className="text-center text-xs text-gray-500 mt-6" data-id="element-256">
                    All donations are processed securely through trusted payment
                    providers
                  </p>
                </> : <>
                  {/* Crypto Wallet Step */}
                  <div className="text-center mb-8" data-id="element-257">
                    <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: 0.1,
                  type: 'spring',
                  stiffness: 200
                }} className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border border-orange-500/30 mb-4" data-id="element-258">
                      <Coins size={32} className="text-orange-400" data-id="element-259" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-white mb-2 font-special" data-id="element-260">
                      Cryptocurrency Donation
                    </h2>
                    <p className="text-gray-400 text-sm" data-id="element-261">
                      Send your donation to our wallet address
                    </p>
                  </div>

                  {/* Wallet Address */}
                  <div className="mb-6" data-id="element-262">
                    <label className="block text-sm font-medium text-gray-300 mb-3" data-id="element-263">
                      Wallet Address (ETH/ERC-20)
                    </label>
                    <div className="relative" data-id="element-264">
                      <input type="text" value={CRYPTO_WALLET_ADDRESS} readOnly className="w-full px-4 py-3 pr-12 rounded-lg bg-white/5 border border-white/10 text-white font-mono text-sm focus:outline-none focus:border-orange-500/50 transition-all" data-id="element-265" />
                      <button onClick={handleCopyAddress} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-orange-500/30 transition-all group" data-id="element-266">
                        {copied ? <Check size={18} className="text-green-400" data-id="element-267" /> : <Copy size={18} className="text-gray-400 group-hover:text-orange-400" data-id="element-268" />}
                      </button>
                    </div>
                    {copied && <motion.p initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-sm text-green-400 mt-2" data-id="element-269">
                        ✓ Address copied to clipboard!
                      </motion.p>}
                  </div>

                  {/* Instructions */}
                  <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/20 mb-6" data-id="element-270">
                    <h3 className="text-sm font-bold text-orange-400 mb-2" data-id="element-271">
                      How to donate:
                    </h3>
                    <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside" data-id="element-272">
                      <li data-id="element-273">Copy the wallet address above</li>
                      <li data-id="element-274">Open your crypto wallet app</li>
                      <li data-id="element-275">
                        Send ETH, USDT, USDC or any ERC-20 token to this address
                      </li>
                      <li data-id="element-276">Your donation will be received instantly</li>
                    </ol>
                  </div>

                  {/* Back Button */}
                  <button onClick={handleBack} className="w-full py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white font-medium transition-all" data-id="element-277">
                    ← Back to payment methods
                  </button>
                </>}
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </AnimatePresence>;
  return createPortal(modalContent, document.body);
}